import type { PrimitiveType, PrimitiveTypeName } from '#layers/data-model/types/schema/primitive-type';
import type { ResolvedMonospaceCollection, ResolvedMonospaceDataSource } from '#layers/data-model/types/schema/resolved-schema';
import type { MonospaceCollectionMeta } from '#layers/data-model/types/schema/workspace-schema';
import { formatTitle } from '@directus/format-title';
import { useI18n } from 'vue-i18n';
import { getTypename } from '#layers/data-model/utils/schema/primitive-type';
import { useTranslationUtils } from '#layers/i18n/public';

export interface FieldDisplayNameInput {
	apiName: string;
	meta?: { displayName?: string | null } | null;
}

export function useDataModelDisplayNames() {
	const { translateString } = useTranslationUtils();
	const { te, t } = useI18n();

	function getFieldDisplayName(field: FieldDisplayNameInput) {
		const raw = field.meta?.displayName ?? formatTitle(field.apiName);
		return translateString(raw);
	}

	function getPrimitiveFieldDisplayName(field: FieldDisplayNameInput) {
		return getFieldDisplayName(field);
	}

	function getSingleRelationFieldDisplayName(field: FieldDisplayNameInput) {
		return getFieldDisplayName(field);
	}

	function getCollectionDisplayName(
		collection: Pick<ResolvedMonospaceCollection, 'apiName'> & {
			meta?: Pick<MonospaceCollectionMeta, 'displayName'> | null;
		},
	) {
		const raw = collection.meta?.displayName ?? formatTitle(collection.apiName);
		return translateString(raw);
	}

	function getDataSourceDisplayName(source: Pick<ResolvedMonospaceDataSource, 'apiName'>) {
		return translateString(formatTitle(source.apiName));
	}

	function getPrimitiveTypeDisplayName(type: PrimitiveType | PrimitiveTypeName) {
		const typename = getTypename(type);
		const key = `types.${typename}`;
		return te(key) ? t(key) : typename;
	}

	return {
		getPrimitiveFieldDisplayName,
		getSingleRelationFieldDisplayName,
		getCollectionDisplayName,
		getDataSourceDisplayName,
		getPrimitiveTypeDisplayName,
		getFieldDisplayName,
	};
}
